import * as React from 'react';
import { ShieldCheck, Trash2, Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import {
  useAddMyApplePayDomain,
  useProbeMyApplePayDomain,
  useRemoveMyApplePayDomain,
  useMyDomainAssociationFile,
  useUpdateMyDomainAssociationFile,
} from '@/lib/api/apple-pay-registrations';
import { DomainAssociationFileDialog } from '@/components/apple-pay/DomainAssociationFileDialog';
import { formatDate } from '@/components/apple-pay/byok-ui';
import type { MyApplePayRegistration } from './types';

export function MerchantDomains({ registration }: { registration: MyApplePayRegistration }) {
  const add = useAddMyApplePayDomain();
  const probe = useProbeMyApplePayDomain();
  const remove = useRemoveMyApplePayDomain();
  const [newDomain, setNewDomain] = React.useState('');
  const [newFile, setNewFile] = React.useState('');
  const [fileDomain, setFileDomain] = React.useState<{ id: string; domain: string } | null>(null);

  const handleAdd = () => {
    const domain = newDomain.trim();
    if (!domain) return;
    add.mutate(
      { registrationId: registration.id, domain, associationFileContent: newFile.trim() || undefined },
      {
        onSuccess: () => {
          setNewDomain('');
          setNewFile('');
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Domains ({registration.domains.length})</h3>

      {registration.domains.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Add the checkout domain(s) where Apple Pay should appear.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Domain</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead className="w-[220px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {registration.domains.map((domain) => (
              <TableRow key={domain.id}>
                <TableCell className="font-mono text-sm">{domain.domain}</TableCell>
                <TableCell>
                  {domain.active ? (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Pending verification</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {domain.hasAssociationFile ? (
                    <Badge variant="outline" className="text-green-700">
                      Set
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      Missing
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {formatDate(domain.verifiedAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFileDomain({ id: domain.id, domain: domain.domain })}
                      title="View / replace association file"
                    >
                      <FileText className="mr-1 h-3.5 w-3.5" />
                      File
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => probe.mutate({ domainId: domain.id })}
                      disabled={probe.isPending}
                    >
                      <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                      {domain.active ? 'Re-check' : 'Verify'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive h-8 w-8"
                      onClick={() => remove.mutate({ domainId: domain.id })}
                      disabled={remove.isPending}
                      title="Remove domain"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div className="bg-muted/30 space-y-2 rounded-md border p-4">
        <p className="text-sm font-medium">Add a domain</p>
        <p className="text-muted-foreground text-xs">
          After adding, verify the domain in the Apple Developer portal, then click <strong>Verify</strong>{' '}
          here to activate it.
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="checkout.example.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="max-w-sm font-mono text-sm"
          />
        </div>
        <Textarea
          placeholder="Paste this domain's apple-developer-merchantid-domain-association file (optional — you can add it later)…"
          value={newFile}
          onChange={(e) => setNewFile(e.target.value)}
          className="min-h-[90px] font-mono text-xs"
        />
        <p className="text-muted-foreground text-xs">
          Apple gives a <strong>different file for each domain</strong> — paste the one for this domain.
        </p>
        <Button onClick={handleAdd} disabled={!newDomain.trim() || add.isPending}>
          <Plus className="mr-2 h-4 w-4" />
          Add domain
        </Button>
      </div>

      {fileDomain && (
        <DomainAssociationFileDialog
          domainId={fileDomain.id}
          domain={fileDomain.domain}
          open={!!fileDomain}
          onOpenChange={(o) => !o && setFileDomain(null)}
          useFileQuery={useMyDomainAssociationFile}
          useUpdateFile={useUpdateMyDomainAssociationFile}
        />
      )}
    </div>
  );
}
